<template>
	<transition name="fade">
		<div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" @click.self="$emit('close')">
			<div class="flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center">
				<div class="fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50" @click="$emit('close')"></div>
				<div
					class="inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-4xl sm:w-full">
					<!-- Modal Header -->
					<div
					class="bg-gradient-to-tr from-primary to-secondary dark:from-primary-light/10 dark:to-secondary-light/10 px-4 py-4 sm:px-6"
					>
						<h3 class="text-lg leading-6 font-medium text-white">
							{{ isEditMode ? 'Edit Employee' : employee ? 'Employee Details' : 'Add New Employee' }}
						</h3>
					</div>

					<!-- Modal Body -->
					<div class="bg-white dark:bg-gray-800 px-3 pt-4 pb-3 sm:p-6 sm:pb-4 overflow-y-auto max-h-[calc(100vh-12rem)]">
						<div class="sm:flex sm:items-start">
							<div class="mt-3 text-left sm:mt-0 w-full">
								<!-- Creation/Edit Form -->
								<form v-if="!employee || isEditMode" @submit.prevent="handleSubmit" class="space-y-6">
									<!-- Personal Info -->
									<div class="bg-gray-50 dark:bg-gray-700/30 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-600">
										<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Personal Information</h4>
										<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
											<div>
												<label class="label">First Name <span class="text-danger">*</span></label>
												<input v-model="formData.first_name" @input="clearLocalError('first_name')" type="text" class="input w-full" placeholder="Enter first name" />
												<p v-if="!employee && fieldError('first_name')" class="error-message">{{ fieldError('first_name') }}</p>
											</div>
											<div>
												<label class="label">Last Name <span class="text-danger">*</span></label>
												<input v-model="formData.last_name" @input="clearLocalError('last_name')" type="text" class="input w-full" placeholder="Enter last name" />
												<p v-if="!employee && fieldError('last_name')" class="error-message">{{ fieldError('last_name') }}</p>
											</div>
											<div>
												<label class="label">Date of Birth</label>
												<input v-model="formData.date_of_birth" type="date" class="input w-full" placeholder="mm / dd / yyyy" />
											</div>
											<div>
												<label class="label">Gender</label>
												<select v-model="formData.gender" class="input w-full">
													<option value="" selected>Select Gender</option>
													<option value="male">Male</option>
													<option value="female">Female</option>
													<option value="other">Other</option>
												</select>
											</div>
											<div>
												<label class="label">Phone Number</label>
												<input v-model="formData.phone" type="tel" class="input w-full" placeholder="Enter phone number" />
											</div>
										</div>
									</div>

									<!-- Address -->
									<div class="bg-gray-50 dark:bg-gray-700/30 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-600">
										<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Address</h4>
										<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
											<div class="md:col-span-2">
												<label class="label">Street</label>
												<input v-model="formData.street" type="text" class="input w-full" placeholder="Enter street address" />
											</div>
											<div>
												<label class="label">City</label>
												<input v-model="formData.city" type="text" class="input w-full" placeholder="Enter city" />
											</div>
											<div>
												<label class="label">State</label>
												<select v-model="formData.state" class="input w-full">
													<option value="">Select a state</option>
													<option
													v-for="state in usStates"
													:key="state"
													:value="state"
													>
													{{ state }}
													</option>
												</select>
											</div>
											<div>
												<label class="label">Zipcode</label>
												<input v-model="formData.zipcode" type="text" class="input w-full" placeholder="Enter zipcode" />
											</div>
										</div>
									</div>

									<!-- Account Info -->
									<div class="bg-gray-50 dark:bg-gray-700/30 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-600">
										<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Account Information</h4>
										<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
											<div>
												<label class="label">Position <span class="text-danger">*</span></label>
												<select v-model="formData.position_id" @change="clearLocalError('position_id')" class="input w-full">
													<option value="" selected>Select Position</option>
													<option v-for="pos in positions" :key="pos.id" :value="pos.id">{{ pos.name }}</option>
												</select>
												<p v-if="!employee && fieldError('position_id')" class="error-message">{{ fieldError('position_id') }}</p>
											</div>
											<div>
												<label class="label">Email <span class="text-danger">*</span></label>
												<input v-model="formData.email" @input="clearLocalError('email')" type="email" class="input w-full" placeholder="Enter email address" />
												<p v-if="!employee && fieldError('email')" class="error-message">{{ fieldError('email') }}</p>
											</div>
											<div class="md:col-span-2">
												<label class="label">Username <span class="text-danger">*</span></label>
												<input v-model="formData.username" @input="clearLocalError('username'); formData.username = $event.target.value.replace(/\s+/g, '')" type="text" class="input w-full" placeholder="Enter username" />
												<p v-if="!employee && fieldError('username')" class="error-message">{{ fieldError('username') }}</p>
											</div>
											<div>
												<div v-if="!isEditMode">
													<div>
														<label class="label">Password <span class="text-danger">*</span></label>
														<div class="relative">
															<input
															v-model="formData.password"
															@input="clearLocalError('password')"
															:type="showPassword ? 'text' : 'password'"
															placeholder="Enter password (min. 8 characters)"
															class="input w-full"
															/>
															<button
																type="button"
																@click="showPassword = !showPassword"
																class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
															>
																<component :is="showPassword ? EyeOff : Eye" class="h-5 w-5" />
															</button>
														</div>
														<p v-if="!employee && fieldError('password')" class="error-message">
															{{ fieldError('password') }}
														</p>
													</div>
												</div>
												<div v-else>
													<label class="label">Password</label>
													<div class="relative">
														<input
															v-model="formData.password"
															@input="clearLocalError('password')"
															:type="showPassword ? 'text' : 'password'"
															placeholder="Leave blank to keep current password"
															class="input w-full"
														/>
														<button
															type="button"
															@click="showPassword = !showPassword"
															class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
														>
															<component :is="showPassword ? EyeOff : Eye" class="h-5 w-5" />
														</button>
													</div>
													<p v-if="!employee && fieldError('password')" class="error-message">
														{{ fieldError('password') }}
													</p>
													<p class="text-xs text-secondary-text/60 dark:text-secondary-dark-text/60 mt-1">Leave blank to keep the current password</p>
												</div>
											</div>
											<div v-if="formData.password">
												<div>
													<label class="label">Confirm Password <span class="text-danger">*</span></label>
													<div class="relative">
														<input
															v-model="formData.password_confirmation"
															@input="clearLocalError('password_confirmation')"
															:type="showConfirmPassword ? 'text' : 'password'"
															placeholder="Confirm password"
															class="input w-full"
														/>
														<button
															type="button"
															@click="showConfirmPassword = !showConfirmPassword"
															class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
														>
															<component :is="showConfirmPassword ? EyeOff : Eye" class="h-5 w-5" />
														</button>
													</div>
													<p v-if="!employee && fieldError('password_confirmation')" class="error-message">
														{{ fieldError('password_confirmation') }}
													</p>
												</div>
											</div>
										</div>
									</div>
								</form>

								<!-- View Details - Same layout as edit form but disabled -->
								<form v-else-if="employee && !isEditMode" class="space-y-6">
									<!-- Status & Meta Info -->
									<div class="bg-gray-50 dark:bg-gray-700/30 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-600">
										<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Status & Meta</h4>
										<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
											<div>
												<label class="label">Status</label>
												<input :value="employee.status ? employee.status.charAt(0).toUpperCase() + employee.status.slice(1) : ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">Date Created</label>
												<input :value="employee.created_at ? new Date(employee.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''" type="text" class="input w-full" disabled />
											</div>
										</div>
									</div>

									<!-- Personal Info -->
									<div class="bg-gray-50 dark:bg-gray-700/30 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-600">
										<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Personal Information</h4>
										<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
											<div>
												<label class="label">First Name</label>
												<input :value="employee.first_name || ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">Last Name</label>
												<input :value="employee.last_name || ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">Date of Birth</label>
												<input :value="employee.date_of_birth || ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">Gender</label>
												<input :value="employee.gender ? employee.gender.charAt(0).toUpperCase() + employee.gender.slice(1) : ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">Phone Number</label>
												<input :value="employee.phone || ''" type="tel" class="input w-full" disabled />
											</div>
										</div>
									</div>

									<!-- Address -->
									<div class="bg-gray-50 dark:bg-gray-700/30 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-600">
										<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Address</h4>
										<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
											<div class="md:col-span-2">
												<label class="label">Street</label>
												<input :value="employee.street || ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">City</label>
												<input :value="employee.city || ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">State</label>
												<input :value="employee.state || ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">Zipcode</label>
												<input :value="employee.zipcode || ''" type="text" class="input w-full" disabled />
											</div>
										</div>
									</div>

									<!-- Account Info -->
									<div class="bg-gray-50 dark:bg-gray-700/30 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-600">
										<h4 class="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Account Information</h4>
										<div class="grid grid-cols-1 md:grid-cols-2 gap-5">
											<div>
												<label class="label">Position</label>
												<input :value="employee.position_id || employee.position || ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">Email</label>
												<input :value="employee.email || ''" type="email" class="input w-full" disabled />
											</div>
											<div class="md:col-span-2">
												<label class="label">Username</label>
												<input :value="employee.username || ''" type="text" class="input w-full" disabled />
											</div>
											<div>
												<label class="label">Password</label>
												<div class="relative">
													<input
														:type="showViewPassword ? 'text' : 'password'"
														class="input w-full"
														:value="employee.visible_password || '********'"
														disabled
													/>
													<button
														type="button"
														@click="showViewPassword = !showViewPassword"
														class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
													>
														<component :is="showViewPassword ? EyeOff : Eye" class="h-5 w-5" />
													</button>
												</div>
												<p class="text-xs text-secondary-text/60 dark:text-secondary-dark-text/60 mt-1">Password provided to employee</p>
											</div>
										</div>
									</div>
								</form>
							</div>
						</div>
					</div>
					<div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0">
						<button v-if="!employee || isEditMode" type="submit" @click="handleSubmit" :disabled="loading"
							class="btn btn-primary w-full sm:w-auto sm:ml-3 flex items-center justify-center">
							<Loader2 v-if="loading" class="w-4 h-4 mr-2 animate-spin" />
							<span v-if="loading">
							{{ isEditMode ? 'Updating...' : 'Creating...' }}
							</span>
							<span v-else>
							{{ isEditMode ? 'Update Employee' : 'Create Employee' }}
							</span>
						</button>
						<button v-if="employee && !isEditMode" type="button" @click="startEdit"
							class="btn btn-info w-full sm:w-auto sm:ml-3">
							Edit
						</button>
						<button type="button" @click="handleClose"
							class="btn btn-secondary bg-danger hover:bg-slate-700 w-full sm:w-auto sm:ml-3">
							{{ isEditMode ? 'Cancel' : 'Close' }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import type { Employee, CreateEmployeeData, UpdateEmployeeData } from '@/services/employeeService'
import { Eye, EyeOff, Loader2 } from 'lucide-vue-next'

const props = defineProps<{
  isOpen: boolean
  employee: Employee | null
  editMode?: boolean
  validationErrors?: Record<string, string>
  existingEmployees?: Employee[]
  positions?: { id: number, name: string }[]
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', data: CreateEmployeeData & { password_confirmation?: string }): void
  (e: 'update', id: number, data: any): void
  (e: 'edit'): void
}>()

// Form data for creating new employee
const formData = reactive<CreateEmployeeData & { password_confirmation?: string }>({
	first_name: '',
	last_name: '',
	username: '',
	email: '',
	password: '',
	password_confirmation: '',
	role: 'employee',
	status: 'active',
	position_id: '',
	phone: '',
	date_of_birth: '',
	gender: '',
	street: '',
	city: '',
	state: '',
	zipcode: ''
})

const showPassword = ref(false)
const showConfirmPassword = ref(false)
const showViewPassword = ref(false)

const isEditMode = ref(false)
const localValidationErrors = ref<Record<string, string>>({})

const fieldError = (field: string): string => {
	if (props.employee) return ''
	return localValidationErrors.value[field] || props.validationErrors?.[field] || ''
}

const clearLocalError = (field: string) => {
	if (localValidationErrors.value[field]) {
		delete localValidationErrors.value[field]
	}
}

// Populate form with employee data
const populateFormFromEmployee = (emp: Employee) => {
	formData.first_name = emp.first_name || ''
	formData.last_name = emp.last_name || ''
	formData.username = emp.username || ''
	formData.email = emp.email || ''
	formData.phone = emp.phone || ''
	formData.date_of_birth = emp.date_of_birth || ''
	formData.gender = emp.gender
	formData.street = emp.address || emp.street || ''
	formData.city = emp.city || ''
	formData.state = emp.state || ''
	formData.zipcode = emp.zipcode || ''
	formData.role = emp.role || 'employee'
	formData.status = emp.status || 'active'
	formData.position_id = emp.position_id
	// Don't populate password fields
	formData.password = ''
	formData.password_confirmation = ''
}

// Reset form to initial state
const resetForm = () => {
	formData.first_name = ''
	formData.last_name = ''
	formData.username = ''
	formData.email = ''
	formData.password = ''
	formData.password_confirmation = ''
	formData.role = 'employee'
	formData.status = 'active'
	formData.position_id = ''
	formData.phone = ''
	formData.date_of_birth = ''
	formData.gender = ''
	formData.street = ''
	formData.city = ''
	formData.state = ''
	formData.zipcode = ''
	localValidationErrors.value = {}
}

// Watch for modal open/close and editMode prop
watch(() => props.isOpen, (isOpen) => {
	if (isOpen) {
		if (props.editMode && props.employee) {
			isEditMode.value = true
			populateFormFromEmployee(props.employee)
		} else if (props.employee && !props.editMode) {
			isEditMode.value = false
		} else if (!props.employee) {
			isEditMode.value = false
			resetForm()
		}
	} else {
		isEditMode.value = false
		resetForm()
	}
}, { immediate: true })

// Watch for employee changes and populate form when editing
watch(() => props.employee, (newEmployee) => {
	if (newEmployee && isEditMode.value) {
		populateFormFromEmployee(newEmployee)
	} else if (!newEmployee) {
		resetForm()
		isEditMode.value = false
	}
}, { immediate: true })

// Watch for editMode prop changes
watch(() => props.editMode, (editMode) => {
	if (editMode && props.employee) {
		isEditMode.value = true
		populateFormFromEmployee(props.employee)
	} else if (!editMode && props.employee) {
		isEditMode.value = false
	}
})

// Start edit mode
const startEdit = () => {
	if (props.employee) {
		isEditMode.value = true
		populateFormFromEmployee(props.employee)
		emit('edit')
	}
}

// Handle close
const handleClose = () => {
	if (isEditMode.value) {
		isEditMode.value = false
		if (props.employee) {
			// Reset form but don't close modal - go back to view mode
			populateFormFromEmployee(props.employee)
		}
	} else {
		resetForm()
		emit('close')
	}
}

// Handle form submission
const handleSubmit = async () => {
	if (props.employee && isEditMode.value) {
		// Update mode
		const updateData: UpdateEmployeeData = {
			first_name: formData.first_name,
			last_name: formData.last_name,
			username: formData.username,
			email: formData.email,
			phone: formData.phone || undefined,
			date_of_birth: formData.date_of_birth || undefined,
			gender: formData.gender,
			address: formData.street || undefined,
			city: formData.city || undefined,
			state: formData.state || undefined,
			zipcode: formData.zipcode || undefined,
			position_id: formData.position_id
		}

		// Only include password if it's provided
		if (formData.password) {
			(updateData as any).password = formData.password
			if (formData.password_confirmation) {
				(updateData as any).password_confirmation = formData.password_confirmation
			}
		}

		emit('update', props.employee.id, updateData)
	} else if (!props.employee) {
		const errors: Record<string, string> = {}

		const firstName = (formData.first_name || '').trim()
		const lastName = (formData.last_name || '').trim()
		const username = (formData.username || '').trim()
		const email = (formData.email || '').trim()
		const password = formData.password || ''
		const passwordConfirmation = formData.password_confirmation || ''
		const positionId = formData.position_id

		if (!firstName) errors.first_name = 'First Name is required.'
		if (!lastName) errors.last_name = 'Last Name is required.'
		if (!username) errors.username = 'Username is required.'
		if (!email) errors.email = 'Email is required.'
		if (!password) errors.password = 'Password is required.'
		if (!positionId) errors.position_id = 'Position is required.'

		if (email) {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(email)) {
				errors.email = 'Please enter a valid email address.'
			}
		}

		if (password && password.length < 8) {
			errors.password = 'Password must be at least 8 characters long.'
		}

		if (password) {
			if (!passwordConfirmation) {
				errors.password_confirmation = 'Please confirm your password.'
			} else if (password !== passwordConfirmation) {
				errors.password_confirmation = 'Passwords do not match.'
			}
		}

		const existingEmployees = props.existingEmployees || []
		const normalizedUsername = username.toLowerCase()
		const normalizedEmail = email.toLowerCase()

		if (normalizedUsername && existingEmployees.some((item) => (item.username || '').toLowerCase() === normalizedUsername)) {
			errors.username = 'Username is already taken.'
		}

		if (normalizedEmail && existingEmployees.some((item) => (item.email || '').toLowerCase() === normalizedEmail)) {
			errors.email = 'Email is already taken.'
		}

		localValidationErrors.value = errors
		if (Object.keys(errors).length > 0) {
			localValidationErrors.value = errors
			return
		}

		emit('save', {
			...formData,
			first_name: firstName,
			last_name: lastName,
			username,
			email
		})
	}
}

const usStates = [
	'Alabama',
	'Alaska',
	'Arizona',
	'Arkansas',
	'California',
	'Colorado',
	'Connecticut',
	'Delaware',
	'Florida',
	'Georgia',
	'Hawaii',
	'Idaho',
	'Illinois',
	'Indiana',
	'Iowa',
	'Kansas',
	'Kentucky',
	'Louisiana',
	'Maine',
	'Maryland',
	'Massachusetts',
	'Michigan',
	'Minnesota',
	'Mississippi',
	'Missouri',
	'Montana',
	'Nebraska',
	'Nevada',
	'New Hampshire',
	'New Jersey',
	'New Mexico',
	'New York',
	'North Carolina',
	'North Dakota',
	'Ohio',
	'Oklahoma',
	'Oregon',
	'Pennsylvania',
	'Rhode Island',
	'South Carolina',
	'South Dakota',
	'Tennessee',
	'Texas',
	'Utah',
	'Vermont',
	'Virginia',
	'Washington',
	'West Virginia',
	'Wisconsin',
	'Wyoming',
]
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
	transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
	opacity: 0;
}
</style> 